/**
 * ViatorExperienceContent.js
 *
 * Content section for Viator experience cards in the social feed.
 * Displays rating, price, duration, category badges, and location.
 */

import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../shared/Icon';
import { formatCurrency } from '../../../utils/currencyFormat';

/**
 * Star rating display
 */
const StarRating = ({ rating, reviewCount }) => {
  const { t } = useTranslation('feedCard');
  if (!rating) return null;
  return (
    <span
      className="flex items-center gap-1"
      title={t('viatorExperienceContent.starRating.title')}
    >
      <Icon name="starFilled" className="w-3.5 h-3.5 text-accent" />
      <span className="font-medium text-content-primary">{Number(rating).toFixed(1)}</span>
      {!!reviewCount && (
        <span className="text-content-muted">({reviewCount.toLocaleString()})</span>
      )}
    </span>
  );
};

/**
 * Main ViatorExperienceContent Component
 */
const ViatorExperienceContent = ({ data }) => {
  const { t } = useTranslation('feedCard');

  /**
   * Format price with currency symbol
   */
  const formatPrice = (price, currency) => {
    if (!price) return null;
    return t('viatorExperienceContent.priceFrom', { price: formatCurrency(price, currency) });
  };

  /**
   * Format duration for display
   */
  const formatDuration = (minutes, text) => {
    if (text) return text;
    if (!minutes) return null;
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0
        ? t('viatorExperienceContent.duration.hoursMinutes', { hours, mins })
        : t('viatorExperienceContent.duration.hoursOnly', { hours });
    }
    return t('viatorExperienceContent.duration.minutesOnly', { minutes });
  };

  const { title, description, location, typeData } = data;
  const {
    rating,
    reviewCount = 0,
    priceFrom,
    priceCurrency,
    durationMinutes,
    durationText,
    primaryCategory,
    freeCancellation,
    likelyToSellOut,
    skipTheLine,
    destinationName,
    country
  } = typeData || {};

  const priceDisplay = formatPrice(priceFrom, priceCurrency);
  const durationDisplay = formatDuration(durationMinutes, durationText);

  return (
    <div className="flex-grow">
      {/* Title */}
      <h3 className="font-semibold text-content-primary mb-2 line-clamp-1">
        <Icon name="compass" className="w-4 h-4 inline mr-1" />{title}
      </h3>

      {/* Stats row: rating, duration, price */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-content-secondary mb-2">
        <StarRating rating={rating} reviewCount={reviewCount} />
        {durationDisplay && (
          <span className="flex items-center">
            <Icon name="clock" className="w-3.5 h-3.5 mr-1" />
            {durationDisplay}
          </span>
        )}
        {priceDisplay && (
          <span className="font-medium text-content-primary">{priceDisplay}</span>
        )}
      </div>

      {/* Badges row */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {primaryCategory && (
          <span className="px-2 py-0.5 bg-info-light text-info-dark text-xs rounded-full">
            {primaryCategory}
          </span>
        )}
        {freeCancellation && (
          <span className="px-2 py-0.5 bg-success-light text-success text-xs rounded-full">
            {t('viatorExperienceContent.badges.freeCancellation')}
          </span>
        )}
        {likelyToSellOut && (
          <span className="px-2 py-0.5 bg-warning-light text-warning-dark text-xs rounded-full">
            {t('viatorExperienceContent.badges.likelyToSellOut')}
          </span>
        )}
        {skipTheLine && (
          <span className="px-2 py-0.5 bg-info-light text-info-dark text-xs rounded-full">
            {t('viatorExperienceContent.badges.skipTheLine')}
          </span>
        )}
      </div>

      {/* Location */}
      {(location || (destinationName && country)) && (
        <p className="text-sm text-content-secondary mb-2 line-clamp-1">
          <Icon name="mapPin" className="w-3.5 h-3.5 inline mr-1" />
          {location || `${destinationName}, ${country}`}
        </p>
      )}

      {/* Description */}
      {description && (
        <p className="text-sm text-content-secondary mb-3 line-clamp-2">
          {description}
        </p>
      )}
    </div>
  );
};

export default memo(ViatorExperienceContent);
